/**
 * Pure, deterministic layout of the far planes (no three.js): ridges, islands, town, window schedule, wires,
 * leaf cards. Geometry builders in src/render/far/*.ts turn these numbers into meshes.
 * Polar placement: azimuth (deg, compass) + horizontal distance (m) from the platform.
 */
import { hash01 } from '../../world/random/hash';
import { fbm1D } from '../../world/random/noise';
import type { Vec3 } from '../frame';

export interface RidgeLayer {
  distanceM: number;
  baseM: number;
  amplitudeM: number;
}

/** Back to front: far haze ridges first. Heights are above sea level. */
export const RIDGE_LAYERS: RidgeLayer[] = [
  { distanceM: 30_000, baseM: 250, amplitudeM: 700 },
  { distanceM: 15_000, baseM: 150, amplitudeM: 500 },
  { distanceM: 8_000, baseM: 60, amplitudeM: 320 },
];

/** The azimuth sector the ridge silhouettes span (buildRidges); outside it the skyline is the sea horizon. */
export const RIDGE_AZ_FROM = 190;
export const RIDGE_AZ_TO = 310;

function ridgeHeightAt(seed: number, layer: number, az: number): number {
  const l = RIDGE_LAYERS[layer];
  if (!l) throw new RangeError(`no ridge layer ${layer}`);
  return l.baseM + l.amplitudeM * fbm1D(seed, `ridge${layer}`, az / 6, 5);
}

export function ridgeHeights(seed: number, layer: number, azFromDeg: number, azToDeg: number, samples: number): number[] {
  return Array.from({ length: samples }, (_, i) => ridgeHeightAt(seed, layer, azFromDeg + ((azToDeg - azFromDeg) * i) / (samples - 1)));
}

export interface Island {
  azDeg: number;
  distanceM: number;
  halfWidthDeg: number;
  peakM: number;
  /** The island whose peak sits in the torii opening (equinox "diamond" sunset). */
  aligned: boolean;
}

export function islands(seed: number, toriiAzDeg: number, count = 6): Island[] {
  const out: Island[] = [{ azDeg: toriiAzDeg, distanceM: 6_000, halfWidthDeg: 3, peakM: 280, aligned: true }];
  for (let i = 0; out.length < count && i < 200; i++) {
    const azDeg = 225 + 60 * hash01(seed, 'isl-az', i);
    const halfWidthDeg = 1.5 + 3.5 * hash01(seed, 'isl-w', i);
    if (Math.abs(azDeg - toriiAzDeg) < halfWidthDeg + 4) continue; // keep the torii window clean
    out.push({
      azDeg,
      distanceM: 2_500 + 9_500 * hash01(seed, 'isl-d', i),
      halfWidthDeg,
      peakM: 60 + 180 * hash01(seed, 'isl-h', i),
      aligned: false,
    });
  }
  return out;
}

/** The eye's height above the sea (composition EYE y - SEA_LEVEL_Y; tested against it). */
export const EYE_ABOVE_SEA_M = 81.6;

/** Shape of an island's silhouette across its width, 0..1 (buildIslands adds ±6 % roughness on top). */
export function islandShape(u: number): number {
  return Math.pow(Math.max(0, Math.cos((u * Math.PI) / 2)), 0.8);
}

/**
 * Elevation (deg) of the far skyline — ridges and islands — in a compass azimuth, as seen from the eye; the sea
 * horizon's dip where nothing stands. The sun or moon behind it lights neither the sky nor the water.
 */
export function skylineAltDeg(seed: number, toriiAzDeg: number, azDeg: number): number {
  const deg = (rise: number, dist: number): number => (Math.atan(rise / dist) * 180) / Math.PI;
  let alt = -(Math.acos(6_371_000 / (6_371_000 + EYE_ABOVE_SEA_M)) * 180) / Math.PI; // sea horizon dip
  const az = ((azDeg % 360) + 360) % 360;
  if (az >= RIDGE_AZ_FROM && az <= RIDGE_AZ_TO) {
    RIDGE_LAYERS.forEach((l, i) => (alt = Math.max(alt, deg(ridgeHeightAt(seed, i, az) - EYE_ABOVE_SEA_M, l.distanceM))));
  }
  for (const isl of islands(seed, toriiAzDeg)) {
    const u = (az - isl.azDeg) / isl.halfWidthDeg;
    if (Math.abs(u) > 1) continue;
    const dist = isl.aligned ? 6_000 : isl.distanceM;
    alt = Math.max(alt, deg(isl.peakM * islandShape(u) * (isl.aligned ? 1 : 0.94) - EYE_ABOVE_SEA_M, dist));
  }
  return alt;
}

export interface House {
  id: number;
  azDeg: number;
  distanceM: number;
  widthM: number;
  depthM: number;
  floors: number;
}

export const TOWN = { azFromDeg: 228, azToDeg: 290, azStepDeg: 0.9, nearM: 600, farM: 1_500, ringStepM: 50 } as const;

/** One house per polar cell at most, so houses never overlap. The two cells nearest the shore become 4–6 floors. */
export function townLayout(seed: number): House[] {
  const houses: House[] = [];
  let id = 0;
  for (let d = TOWN.nearM; d <= TOWN.farM; d += TOWN.ringStepM) {
    for (let az = TOWN.azFromDeg; az <= TOWN.azToDeg; az += TOWN.azStepDeg) {
      const density = 0.35 + 0.4 * ((d - TOWN.nearM) / (TOWN.farM - TOWN.nearM));
      if (hash01(seed, 'house', d, az) > density) continue;
      const r = hash01(seed, 'floors', d, az);
      houses.push({
        id: id++,
        azDeg: az + (hash01(seed, 'jit-az', d, az) - 0.5) * TOWN.azStepDeg * 0.4,
        distanceM: d + (hash01(seed, 'jit-d', d, az) - 0.5) * TOWN.ringStepM * 0.4,
        widthM: 7 + 4 * hash01(seed, 'w', d, az),
        depthM: 7 + 3 * hash01(seed, 'dp', d, az),
        floors: r < 0.7 ? 1 : r < 0.97 ? 2 : 3,
      });
    }
  }
  const nearShore = [...houses].sort((a, b) => b.distanceM - a.distanceM).slice(0, 2);
  for (const h of nearShore) h.floors = 4 + Math.floor(3 * hash01(seed, 'tall', h.id));
  return houses;
}

export function windowCount(house: House): number {
  return house.floors * 2;
}

/**
 * Deterministic window schedule. A window lights when the sun sinks below its own threshold (+2°…−6°),
 * goes dark at its own bedtime (21:30…02:00), and ~10 % stay lit all night.
 */
export function windowLit(seed: number, windowId: number, localHour: number, sunAltDeg: number): boolean {
  const onAlt = 2 - 8 * hash01(seed, 'win-on', windowId);
  if (sunAltDeg >= onAlt) return false;
  if (hash01(seed, 'win-night', windowId) < 0.1) return true;
  const bedtime = 21.5 + 4.5 * hash01(seed, 'win-off', windowId); // may exceed 24 = after midnight
  const h = localHour < 12 ? localHour + 24 : localHour; // evening-continuous clock
  const wake = 24 + 5.5 + 1.5 * hash01(seed, 'win-wake', windowId);
  return h < bedtime || h >= wake;
}

/** Parabolic approximation of a sagging wire between two supports. */
export function catenary(a: Vec3, b: Vec3, sagM: number, segments: number): Vec3[] {
  return Array.from({ length: segments + 1 }, (_, i) => {
    const t = i / segments;
    return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t - sagM * 4 * t * (1 - t), a[2] + (b[2] - a[2]) * t];
  });
}

export interface LeafCard {
  position: Vec3;
  rotation: Vec3;
  scale: number;
}

/** Leaf/blossom cards scattered in a sphere around each branch tip (deterministic). */
export function leafCards(seed: number, tips: Vec3[], perTip: number, radiusM: number): LeafCard[] {
  const cards: LeafCard[] = [];
  tips.forEach((tip, t) => {
    for (let i = 0; i < perTip; i++) {
      const u = hash01(seed, 'lc-u', t, i);
      const v = hash01(seed, 'lc-v', t, i);
      const w = Math.cbrt(hash01(seed, 'lc-r', t, i));
      const theta = 2 * Math.PI * u;
      const phi = Math.acos(2 * v - 1);
      const r = radiusM * w;
      cards.push({
        position: [tip[0] + r * Math.sin(phi) * Math.cos(theta), tip[1] + r * Math.cos(phi), tip[2] + r * Math.sin(phi) * Math.sin(theta)],
        rotation: [
          2 * Math.PI * hash01(seed, 'lc-rx', t, i),
          2 * Math.PI * hash01(seed, 'lc-ry', t, i),
          2 * Math.PI * hash01(seed, 'lc-rz', t, i),
        ],
        scale: 0.7 + 0.6 * hash01(seed, 'lc-s', t, i),
      });
    }
  });
  return cards;
}
